/**
 * 履歴画面の 1 回きりの取得 (③ 履歴詳細 / 最新との比較) を扱うフック (マスタ変更履歴 SBI 7)。
 *
 * - 初期状態は読み込み中。マウント時に `load` を 1 回呼ぶ。`reload` で取り直す。
 * - 取得条件 (`entity_key` / 版) が変わるときは、呼び出し側が `key` でコンポーネントを
 *   作り直す。effect の中で同期的に state を初期化せずに済み、古い結果が残らない。
 * - アンマウント後・取り直し後に届いた古いレスポンスは捨てる。
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface MasterHistoryLoad<T> {
  data: T | null;
  loading: boolean;
  error: unknown;
  reload: () => void;
}

interface LoadState<T> {
  data: T | null;
  loading: boolean;
  error: unknown;
}

export function useMasterHistoryLoad<T>(load: () => Promise<T>): MasterHistoryLoad<T> {
  const [state, setState] = useState<LoadState<T>>({ data: null, loading: true, error: null });
  const [reloadCount, setReloadCount] = useState(0);
  const loadRef = useRef(load);

  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    loadRef
      .current()
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ data: null, loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [reloadCount]);

  const reload = useCallback((): void => {
    setState({ data: null, loading: true, error: null });
    setReloadCount((n) => n + 1);
  }, []);

  return { ...state, reload };
}
